if (!customElements.get('customer-optin-form')) {
  customElements.define(
    'customer-optin-form',
    class CustomerOptinForm extends HTMLElement {
      constructor() {
        super();
        this.storageKey = 'lpOptinSubmitted';
        this.handleSubmit = this.handleSubmit.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        if (this.form) {
          this.form.addEventListener('submit', this.handleSubmit);
        }
        this.revealScopedMessage();
      }

      disconnectedCallback() {
        if (this.form) {
          this.form.removeEventListener('submit', this.handleSubmit);
        }
      }

      handleSubmit(event) {
        if (this.shouldUseLocalUnlock) {
          event?.preventDefault();

          if (this.form && !this.form.reportValidity()) return;

          this.persistLocalSubscription();
        } else if (this.shouldPersistAccess) {
          // Write the access cookie so a gated return_to page (the Cody Blueprint
          // Library) auto-unlocks with this email, WITHOUT preventing the native
          // Shopify submit + return_to redirect.
          this.persistLocalSubscription();
        }

        try {
          sessionStorage.setItem(this.storageKey, this.dataset.optinId);
        } catch (error) {
          /* sessionStorage unavailable (e.g. privacy mode); native submit still works. */
        }

        this.sendWebhookOnSubmit();

        if (this.shouldUseLocalUnlock) {
          this.dispatchEvent(
            new CustomEvent('customer-optin:local-unlock', {
              bubbles: true,
              detail: {
                email: this.normalizedEmail,
              },
            })
          );
        }
      }

      revealScopedMessage() {
        const message = this.querySelector('[data-optin-message]');
        if (!message) return;

        let submittedId = null;
        try {
          submittedId = sessionStorage.getItem(this.storageKey);
        } catch (error) {
          /* Cannot read sessionStorage; fall back below. */
        }

        const isMatch = submittedId === this.dataset.optinId;
        // Fallback: if we have no stored id (sessionStorage cleared/unavailable)
        // and this is the only opt-in form on the page, there is nothing to
        // isolate from, so reveal it. With multiple forms we stay strict.
        const isOnlyForm =
          submittedId == null &&
          document.querySelectorAll('customer-optin-form').length === 1;

        if (!isMatch && !isOnlyForm) return;

        message.hidden = false;

        try {
          sessionStorage.removeItem(this.storageKey);
        } catch (error) {
          /* no-op */
        }

        message.focus({ preventScroll: false });
        message.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      get emailInput() {
        return this.querySelector('input[name="contact[email]"]');
      }

      get normalizedEmail() {
        return (this.emailInput?.value || '').trim().toLowerCase();
      }

      get shouldUseLocalUnlock() {
        return this.dataset.localUnlock === 'true';
      }

      get shouldPersistAccess() {
        return this.dataset.persistAccess === 'true';
      }

      get localStorageKey() {
        return this.dataset.localStorageKey || 'blueprintAccess';
      }

      get localCookieKey() {
        return encodeURIComponent(this.localStorageKey);
      }

      get shouldPostToWebhook() {
        const tags = (this.dataset.webhookTags || '').split(',').map((tag) => tag.trim());

        return this.dataset.webhookUrl && tags.includes('beehiiv');
      }

      persistLocalSubscription() {
        const email = this.normalizedEmail;
        if (!email) return;

        const accessPayload = {
          email,
          subscribed: true,
          updatedAt: new Date().toISOString(),
        };

        try {
          localStorage.setItem(this.localStorageKey, JSON.stringify(accessPayload));
        } catch (error) {
          /* localStorage unavailable; cookie access below can still unlock. */
        }

        try {
          const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
          document.cookie = `${this.localCookieKey}=${encodeURIComponent(JSON.stringify(accessPayload))}; Max-Age=31536000; Path=/; SameSite=Lax${secureFlag}`;
        } catch (error) {
          /* Cookies unavailable; the webhook still receives the opt-in. */
        }
      }

      sendWebhookOnSubmit() {
        if (!this.shouldPostToWebhook) return;

        const email = this.normalizedEmail;
        if (!email) return;

        try {
          fetch(this.dataset.webhookUrl, {
            method: 'POST',
            keepalive: true,
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email,
              tags: this.dataset.webhookTags,
            }),
          });
        } catch (error) {
          /* Webhook delivery should not block the native Shopify submit. */
        }
      }
    }
  );
}
